import { useEffect, useState } from "react";
import axios from "axios";

const API_BASE = `${process.env.REACT_APP_BACKEND_URL}/api/v2`;

export const DEFAULT_TAX = { gst_enabled: true, gst_rate: 5, gst_label: "GST" };

let _cfg = null;
let _inflight = null;

export async function fetchTaxConfig(force = false) {
  if (_cfg && !force) return _cfg;
  if (!_inflight) {
    _inflight = axios
      .get(`${API_BASE}/outlet/tax`)
      .then(({ data }) => { _cfg = data; return data; })
      .catch(() => DEFAULT_TAX)
      .finally(() => { _inflight = null; });
  }
  return _inflight;
}

export function setTaxConfig(cfg) { if (cfg) _cfg = cfg; }

// Fetch-once hook; returns the shared tax config (default 5% GST until loaded).
export function useTaxConfig() {
  const [cfg, setCfg] = useState(_cfg || DEFAULT_TAX);
  useEffect(() => {
    let alive = true;
    fetchTaxConfig().then((c) => { if (alive) setCfg(c); });
    return () => { alive = false; };
  }, []);
  return cfg;
}

const num = (v) => { const n = Number(v); return Number.isNaN(n) ? 0 : n; };
const round2 = (n) => Math.round((n + Number.EPSILON) * 100) / 100;

// GST-on-top breakdown for a (possibly discounted) base amount.
export function computeGst(baseAmount, cfg = _cfg || DEFAULT_TAX, originalAmount = null) {
  const subtotal = round2(num(baseAmount));
  let initial = originalAmount != null && originalAmount !== ""
    ? round2(num(originalAmount)) : subtotal;
  if (initial < subtotal) initial = subtotal;
  const discount = round2(initial - subtotal);
  const rate = cfg?.gst_enabled ? num(cfg.gst_rate) : 0;
  const gst = round2((subtotal * rate) / 100);
  const total = round2(subtotal + gst);
  return {
    initial, discount, subtotal, gst, total, rate,
    enabled: !!cfg?.gst_enabled, label: cfg?.gst_label || "GST",
    hasDiscount: discount > 0,
  };
}

// Prefer the backend-computed GST fields on a payment; else derive them.
export function breakdownFromPayment(p, cfg = _cfg || DEFAULT_TAX) {
  if (p && p.total != null && p.subtotal != null && p.gst_amount != null) {
    const discount = num(p.discount_amount);
    return {
      initial: num(p.initial_amount ?? p.subtotal),
      discount, subtotal: num(p.subtotal), gst: num(p.gst_amount), total: num(p.total),
      rate: num(p.gst_rate ?? cfg?.gst_rate),
      enabled: p.gst_enabled != null ? !!p.gst_enabled : !!cfg?.gst_enabled,
      label: "GST", hasDiscount: discount > 0,
    };
  }
  return computeGst(p?.amout, cfg, p?.original_amount);
}
